import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Container,
  CssBaseline,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip as MuiTooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PaidIcon from '@mui/icons-material/Paid';
import SavingsIcon from '@mui/icons-material/Savings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RestoreIcon from '@mui/icons-material/Restore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ThemeProvider } from './context/ThemeContext';
import { useNotification } from './context/NotificationContext';
import { ConfirmationProvider, useConfirmation } from './context/ConfirmationContext';
import AddSmsVoipRecordDialog from './sms_voip/components/AddSmsVoipRecordDialog';

const API_BASE = process.env.REACT_APP_SMS_VOIP_API || 'https://frek.cc/proxy/38.54.86.24:8010/api';
const OVERALL_PROVIDER_STORAGE_KEY = 'sms_voip_overall_selected_providers';
const OVERALL_DATE_FILTER_STORAGE_KEY = 'sms_voip_overall_date_filter';
const OVERALL_DAILY_MONTH_STORAGE_KEY = 'sms_voip_overall_daily_month';
const OVERALL_DAILY_YEAR_STORAGE_KEY = 'sms_voip_overall_daily_year';
const INSIGHTS_DATE_FILTER_STORAGE_KEY = 'sms_voip_insights_date_filter';
const INSIGHTS_DAILY_MONTH_STORAGE_KEY = 'sms_voip_insights_daily_month';
const INSIGHTS_DAILY_YEAR_STORAGE_KEY = 'sms_voip_insights_daily_year';
const DELETE_HISTORY_STORAGE_KEY = 'sms_voip_delete_history';
const AUDIT_LOG_STORAGE_KEY = 'sms_voip_audit_log';
const SHOW_BALANCES_STORAGE_KEY = 'sms_voip_show_balances';
const CURRENCY_VIEW_STORAGE_KEY = 'sms_voip_currency_view';

const COLLECTIONS = [
  { value: 'earnings', label: 'Earnings' },
  { value: 'expenses', label: 'Expenses' },
  { value: 'providers', label: 'Providers' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

const FIELD_CONFIG = {
  earnings: ['date', 'provider_code', 'channel', 'currency', 'amount'],
  expenses: ['date', 'type', 'amount', 'sim_rate', 'sim_used'],
  providers: ['code', 'name', 'channels', 'currencies'],
  withdrawn: ['date', 'provider_code', 'channel', 'currency', 'amount'],
};

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

function normalizeDate(value) {
  if (!value) return '';
  if (typeof value === 'string') {
    const direct = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (direct) return direct[1];
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatShortDate(value) {
  const normalized = normalizeDate(value);
  if (!normalized) return '';
  const [year, month, day] = normalized.split('-').map(Number);
  const shortYear = String(year).slice(-2);
  return `${month}/${day}/${shortYear}`;
}

function toDisplayValue(field, value) {
  if (value === null || value === undefined) return '';
  if (field === 'date') return formatShortDate(value);
  if (field === 'amount' || field === 'sim_rate' || field === 'sim_used') {
    return formatAmount(value);
  }
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

function safeNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatAmount(value) {
  return safeNumber(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function toDateRangeByLabel(label) {
  if (!label) return { from: '', to: '' };
  if (/^\d{4}-\d{2}-\d{2}$/.test(label)) return { from: label, to: label };
  if (/^\d{4}-\d{2}$/.test(label)) {
    const [year, month] = label.split('-').map(Number);
    const lastDay = new Date(year, month, 0).getDate();
    return {
      from: `${label}-01`,
      to: `${label}-${String(lastDay).padStart(2, '0')}`,
    };
  }
  if (/^\d{4}$/.test(label)) return { from: `${label}-01-01`, to: `${label}-12-31` };
  return { from: '', to: '' };
}

function SmsVoipManagerContent() {
  const confirm = useConfirmation();
  const { showNotification } = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isNarrow = useMediaQuery('(max-width:360px)');

  const [activeTab, setActiveTab] = useState('dashboard');
  const [insightsView, setInsightsView] = useState(() => {
    try {
      const raw = localStorage.getItem(INSIGHTS_DATE_FILTER_STORAGE_KEY);
      if (raw === 'daily' || raw === 'monthly' || raw === 'yearly') return raw;
      return 'monthly';
    } catch (err) {
      return 'monthly';
    }
  });
  const [insightsDailyMonth, setInsightsDailyMonth] = useState(() => {
    const currentMonth = new Date().getMonth() + 1;
    try {
      const raw = Number(localStorage.getItem(INSIGHTS_DAILY_MONTH_STORAGE_KEY));
      if (Number.isInteger(raw) && raw >= 1 && raw <= 12) return raw;
      return currentMonth;
    } catch (err) {
      return currentMonth;
    }
  });
  const [insightsDailyYear, setInsightsDailyYear] = useState(() => {
    const currentYear = new Date().getFullYear();
    try {
      const raw = Number(localStorage.getItem(INSIGHTS_DAILY_YEAR_STORAGE_KEY));
      if (Number.isInteger(raw) && raw >= 2000 && raw <= 3000) return raw;
      return currentYear;
    } catch (err) {
      return currentYear;
    }
  });
  const [recordsTab, setRecordsTab] = useState('records');
  const [collection, setCollection] = useState('earnings');
  const [search, setSearch] = useState('');
  const [recordDateFrom, setRecordDateFrom] = useState('');
  const [recordDateTo, setRecordDateTo] = useState('');
  const [rows, setRows] = useState([]);
  const [recordTotal, setRecordTotal] = useState(0);
  const [recordPage, setRecordPage] = useState(1);
  const [recordPageSize, setRecordPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [dashboardData, setDashboardData] = useState({
    earnings: [],
    expenses: [],
    providers: [],
    withdrawn: [],
  });
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState('');
  const [serverInsights, setServerInsights] = useState({ daily: [], monthly: [], yearly: [] });
  const [serverOverallByDate, setServerOverallByDate] = useState([]);

  const [fxRates, setFxRates] = useState({ usdToPhp: null, eurToPhp: null, lastUpdated: '' });
  const [fxLoading, setFxLoading] = useState(false);
  const [fxError, setFxError] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showBalances, setShowBalances] = useState(() => {
    try {
      const raw = localStorage.getItem(SHOW_BALANCES_STORAGE_KEY);
      if (raw === null) return true;
      return raw === '1';
    } catch (err) {
      return true;
    }
  });
  const compactMode = true;
  const [currencyView] = useState(() => {
    try {
      const raw = localStorage.getItem(CURRENCY_VIEW_STORAGE_KEY);
      if (raw === 'phpOnly' || raw === 'originalPlusPhp') return raw;
      return 'originalPlusPhp';
    } catch (err) {
      return 'originalPlusPhp';
    }
  });
  const [filterDateFrom] = useState('');
  const [filterDateTo] = useState('');
  const [filterProviderCode] = useState('ALL');
  const [filterChannel] = useState('ALL');
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState('asc');
  const [overallSortField, setOverallSortField] = useState('date');
  const [overallSortOrder, setOverallSortOrder] = useState('asc');
  const [overallDateFilter, setOverallDateFilter] = useState(() => {
    try {
      const raw = localStorage.getItem(OVERALL_DATE_FILTER_STORAGE_KEY);
      if (raw === 'all' || raw === 'daily' || raw === 'monthly' || raw === 'yearly') return raw;
      return 'all';
    } catch (err) {
      return 'all';
    }
  });
  const [overallDailyMonth, setOverallDailyMonth] = useState(() => {
    const currentMonth = new Date().getMonth() + 1;
    try {
      const raw = Number(localStorage.getItem(OVERALL_DAILY_MONTH_STORAGE_KEY));
      if (Number.isInteger(raw) && raw >= 1 && raw <= 12) return raw;
      return currentMonth;
    } catch (err) {
      return currentMonth;
    }
  });
  const [overallDailyYear, setOverallDailyYear] = useState(() => {
    const currentYear = new Date().getFullYear();
    try {
      const raw = Number(localStorage.getItem(OVERALL_DAILY_YEAR_STORAGE_KEY));
      if (Number.isInteger(raw) && raw >= 2000 && raw <= 3000) return raw;
      return currentYear;
    } catch (err) {
      return currentYear;
    }
  });
  const [undoDelete, setUndoDelete] = useState({ open: false, record: null, collection: '' });
  const [deleteHistory, setDeleteHistory] = useState(() => {
    try {
      const raw = localStorage.getItem(DELETE_HISTORY_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  });
  const [auditLog, setAuditLog] = useState(() => {
    try {
      const raw = localStorage.getItem(AUDIT_LOG_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  });
  const searchInputRef = useRef(null);
  const [selectedOverallProviders, setSelectedOverallProviders] = useState(() => {
    try {
      const raw = localStorage.getItem(OVERALL_PROVIDER_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch (err) {
      return null;
    }
  });

  const fields = useMemo(() => FIELD_CONFIG[collection], [collection]);

  const buildQueryString = (params) => {
    const q = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      q.append(key, String(value));
    });
    return q.toString();
  };

  const fetchCollection = async (name, params = {}) => {
    const query = buildQueryString(params);
    const response = await fetch(`${API_BASE}/${name}${query ? `?${query}` : ''}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || `Failed to fetch ${name}`);
    return data;
  };

  const fetchRows = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        limit: recordPageSize,
        skip: (recordPage - 1) * recordPageSize,
        search,
        date_from: recordDateFrom,
        date_to: recordDateTo,
        sort_field: sortField,
        sort_order: sortOrder,
      };
      if (collection === 'earnings' || collection === 'withdrawn') {
        params.provider_code = filterProviderCode !== 'ALL' ? filterProviderCode : '';
        params.channel = filterChannel !== 'ALL' ? filterChannel : '';
      }
      const data = await fetchCollection(collection, params);
      setRows(data.items || []);
      setRecordTotal(safeNumber(data.total));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    setDashboardLoading(true);
    setDashboardError('');
    try {
      const reportParams = {
        date_from: filterDateFrom,
        date_to: filterDateTo,
        provider_code: filterProviderCode !== 'ALL' ? filterProviderCode : '',
        channel: filterChannel !== 'ALL' ? filterChannel : '',
        usd_to_php: fxRates.usdToPhp || '',
        eur_to_php: fxRates.eurToPhp || '',
      };

      const [earningsRes, expensesRes, providersRes, withdrawnRes, dailyRes, monthlyRes, yearlyRes, overallRes] = await Promise.all([
        fetchCollection('earnings', {
          limit: 5000,
          date_from: filterDateFrom,
          date_to: filterDateTo,
          provider_code: filterProviderCode !== 'ALL' ? filterProviderCode : '',
          channel: filterChannel !== 'ALL' ? filterChannel : '',
        }),
        fetchCollection('expenses', {
          limit: 5000,
          date_from: filterDateFrom,
          date_to: filterDateTo,
        }),
        fetchCollection('providers', { limit: 2000 }),
        fetchCollection('withdrawn', {
          limit: 5000,
          date_from: filterDateFrom,
          date_to: filterDateTo,
          provider_code: filterProviderCode !== 'ALL' ? filterProviderCode : '',
          channel: filterChannel !== 'ALL' ? filterChannel : '',
        }),
        fetchCollection('reports/insights', { ...reportParams, group_by: 'daily' }),
        fetchCollection('reports/insights', { ...reportParams, group_by: 'monthly' }),
        fetchCollection('reports/insights', { ...reportParams, group_by: 'yearly' }),
        fetchCollection('reports/overall-by-date', reportParams),
      ]);
      setDashboardData({
        earnings: earningsRes.items || [],
        expenses: expensesRes.items || [],
        providers: providersRes.items || [],
        withdrawn: withdrawnRes.items || [],
      });
      setServerInsights({
        daily: dailyRes.items || [],
        monthly: monthlyRes.items || [],
        yearly: yearlyRes.items || [],
      });
      setServerOverallByDate(overallRes.items || []);
    } catch (err) {
      setDashboardError(err.message);
    } finally {
      setDashboardLoading(false);
    }
  };

  const fetchFxRates = async () => {
    setFxLoading(true);
    setFxError('');
    try {
      const response = await fetch('https://open.er-api.com/v6/latest/USD');
      const data = await response.json();
      if (!response.ok || data?.result !== 'success') throw new Error('Failed to fetch live FX rates');
      const usdToPhp = safeNumber(data?.rates?.PHP);
      const usdToEur = safeNumber(data?.rates?.EUR);
      if (!usdToPhp || !usdToEur) throw new Error('FX response missing EUR/PHP rates');
      setFxRates({
        usdToPhp,
        eurToPhp: usdToPhp / usdToEur,
        lastUpdated: data?.time_last_update_utc || '',
      });
    } catch (err) {
      setFxError(err.message);
    } finally {
      setFxLoading(false);
    }
  };

  useEffect(() => {
    setSearch('');
    setRecordDateFrom('');
    setRecordDateTo('');
    setRecordPage(1);
    setEditingRecord(null);
    setSortField(FIELD_CONFIG[collection][0]);
    setSortOrder('asc');
  }, [collection]);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection, recordPage, recordPageSize, search, recordDateFrom, recordDateTo, sortField, sortOrder, filterProviderCode, filterChannel]);

  useEffect(() => {
    fetchDashboard();
    fetchFxRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDateFrom, filterDateTo, filterProviderCode, filterChannel, fxRates.usdToPhp, fxRates.eurToPhp]);

  const filteredDashboardData = useMemo(() => {
    const inDateRange = (value) => {
      const date = normalizeDate(value);
      if (!date) return false;
      if (filterDateFrom && date < filterDateFrom) return false;
      if (filterDateTo && date > filterDateTo) return false;
      return true;
    };
    const earnings = (dashboardData.earnings || []).filter((item) => {
      if (!inDateRange(item.date)) return false;
      if (filterProviderCode !== 'ALL' && (item.provider_code || 'UNKNOWN') !== filterProviderCode) return false;
      if (filterChannel !== 'ALL' && item.channel !== filterChannel) return false;
      return true;
    });
    const withdrawn = (dashboardData.withdrawn || []).filter((item) => {
      if (!inDateRange(item.date)) return false;
      if (filterProviderCode !== 'ALL' && (item.provider_code || 'UNKNOWN') !== filterProviderCode) return false;
      if (filterChannel !== 'ALL' && item.channel !== filterChannel) return false;
      return true;
    });
    const expenses = (dashboardData.expenses || []).filter((item) => inDateRange(item.date));
    return {
      earnings,
      withdrawn,
      expenses,
      providers: dashboardData.providers || [],
    };
  }, [dashboardData, filterDateFrom, filterDateTo, filterProviderCode, filterChannel]);

  const dashboardMetrics = useMemo(() => {
    const earnings = filteredDashboardData.earnings || [];
    const expenses = filteredDashboardData.expenses || [];
    const withdrawn = filteredDashboardData.withdrawn || [];
    const providerSeed = new Set((filteredDashboardData.providers || []).map((p) => p.code));

    let grossSmsEur = 0;
    let grossVoipUsd = 0;
    let withdrawnSmsEur = 0;
    let withdrawnVoipUsd = 0;
    let expenseSimPhp = 0;
    let expenseLoadPhp = 0;

    const providerMap = {};
    const ensureProvider = (code) => {
      const key = code || 'UNKNOWN';
      if (!providerMap[key]) {
        providerMap[key] = {
          provider_code: key,
          earnings_sms_eur: 0,
          earnings_voip_usd: 0,
          withdrawn_sms_eur: 0,
          withdrawn_voip_usd: 0,
        };
      }
      return providerMap[key];
    };

    earnings.forEach((item) => {
      const amount = safeNumber(item.amount);
      const provider = ensureProvider(item.provider_code);
      providerSeed.add(item.provider_code || 'UNKNOWN');
      if (item.channel === 'SMS' && item.currency === 'EUR') {
        grossSmsEur += amount;
        provider.earnings_sms_eur += amount;
      }
      if (item.channel === 'VOIP' && item.currency === 'USD') {
        grossVoipUsd += amount;
        provider.earnings_voip_usd += amount;
      }
    });

    withdrawn.forEach((item) => {
      const amount = safeNumber(item.amount);
      const provider = ensureProvider(item.provider_code);
      providerSeed.add(item.provider_code || 'UNKNOWN');
      if (item.channel === 'SMS' && item.currency === 'EUR') {
        withdrawnSmsEur += amount;
        provider.withdrawn_sms_eur += amount;
      }
      if (item.channel === 'VOIP' && item.currency === 'USD') {
        withdrawnVoipUsd += amount;
        provider.withdrawn_voip_usd += amount;
      }
    });

    expenses.forEach((item) => {
      const amount = safeNumber(item.amount);
      if (item.type === 'sim') expenseSimPhp += amount;
      if (item.type === 'load') expenseLoadPhp += amount;
    });

    providerSeed.forEach((code) => ensureProvider(code));
    const providers = Object.values(providerMap)
      .map((item) => ({
        ...item,
        balance_sms_eur: item.earnings_sms_eur - item.withdrawn_sms_eur,
        balance_voip_usd: item.earnings_voip_usd - item.withdrawn_voip_usd,
      }))
      .sort((a, b) => a.provider_code.localeCompare(b.provider_code));

    const usdToPhp = safeNumber(fxRates.usdToPhp);
    const eurToPhp = safeNumber(fxRates.eurToPhp);
    const toPhp = (amount, currency) => (currency === 'USD' ? safeNumber(amount) * usdToPhp : safeNumber(amount) * eurToPhp);

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const currentDay = now.getDate();
    const daysInCurrentMonth = new Date(currentYear, currentMonth, 0).getDate();

    const getYearMonth = (value) => {
      const normalized = normalizeDate(value);
      if (!normalized) return null;
      const parts = normalized.split('-');
      return { year: Number(parts[0]), month: Number(parts[1]) };
    };

    let currentMonthIncomePhp = 0;
    earnings.forEach((item) => {
      const ym = getYearMonth(item.date);
      if (ym && ym.year === currentYear && ym.month === currentMonth) currentMonthIncomePhp += toPhp(item.amount, item.currency);
    });

    let currentMonthUsedSim = 0;
    let currentMonthSimExpensePhp = 0;
    let currentMonthLoadExpensePhp = 0;
    expenses.forEach((item) => {
      const ym = getYearMonth(item.date);
      if (!ym || ym.year !== currentYear || ym.month !== currentMonth) return;
      if (item.type === 'sim') {
        currentMonthUsedSim += safeNumber(item.sim_used);
        currentMonthSimExpensePhp += safeNumber(item.amount);
      } else if (item.type === 'load') {
        currentMonthLoadExpensePhp += safeNumber(item.amount);
      }
    });

    const dailyAveragePhp = currentDay > 0 ? currentMonthIncomePhp / currentDay : 0;
    const monthProjectionPhp = dailyAveragePhp * daysInCurrentMonth;

    return {
      providers,
      usdToPhp,
      eurToPhp,
      grossSmsEur,
      grossVoipUsd,
      withdrawnSmsEur,
      withdrawnVoipUsd,
      availableSmsEur: grossSmsEur - withdrawnSmsEur,
      availableVoipUsd: grossVoipUsd - withdrawnVoipUsd,
      expenseSimPhp,
      expenseLoadPhp,
      totalExpensePhp: expenseSimPhp + expenseLoadPhp,
      grossEarningsPhp: (grossSmsEur * eurToPhp) + (grossVoipUsd * usdToPhp),
      withdrawnPhp: (withdrawnSmsEur * eurToPhp) + (withdrawnVoipUsd * usdToPhp),
      availableBalancePhp:
        ((grossSmsEur - withdrawnSmsEur) * eurToPhp) +
        ((grossVoipUsd - withdrawnVoipUsd) * usdToPhp),
      currentMonthIncomePhp,
      dailyAveragePhp,
      monthProjectionPhp,
      currentMonthUsedSim,
      currentMonthSimExpensePhp,
      currentMonthLoadExpensePhp,
      totalCurrentMonthExpensePhp: currentMonthSimExpensePhp + currentMonthLoadExpensePhp,
    };
  }, [filteredDashboardData, fxRates]);

  const insightsData = useMemo(() => {
    const usdToPhp = safeNumber(fxRates.usdToPhp);
    const eurToPhp = safeNumber(fxRates.eurToPhp);
    const toPhp = (amount, currency) => (currency === 'USD' ? safeNumber(amount) * usdToPhp : safeNumber(amount) * eurToPhp);

    const grouped = {
      daily: {},
      monthly: {},
      yearly: {},
    };

    const ensure = (target, key) => {
      if (!target[key]) {
        target[key] = {
          key,
          gross_php: 0,
          expenses_php: 0,
          withdrawn_php: 0,
          net_php: 0,
          end_balance_php: 0,
        };
      }
      return target[key];
    };

    const bucketKeys = (dateValue) => {
      const date = normalizeDate(dateValue);
      if (!date) return null;
      return {
        daily: date,
        monthly: date.slice(0, 7),
        yearly: date.slice(0, 4),
      };
    };

    const updateBuckets = (dateValue, updater) => {
      const keys = bucketKeys(dateValue);
      if (!keys) return;
      updater(ensure(grouped.daily, keys.daily));
      updater(ensure(grouped.monthly, keys.monthly));
      updater(ensure(grouped.yearly, keys.yearly));
    };

    (filteredDashboardData.earnings || []).forEach((item) => {
      const amountPhp = toPhp(item.amount, item.currency);
      updateBuckets(item.date, (bucket) => {
        bucket.gross_php += amountPhp;
      });
    });

    (filteredDashboardData.expenses || []).forEach((item) => {
      const amountPhp = safeNumber(item.amount);
      updateBuckets(item.date, (bucket) => {
        bucket.expenses_php += amountPhp;
      });
    });

    (filteredDashboardData.withdrawn || []).forEach((item) => {
      const amountPhp = toPhp(item.amount, item.currency);
      updateBuckets(item.date, (bucket) => {
        bucket.withdrawn_php += amountPhp;
      });
    });

    const finalize = (source) =>
      Object.values(source)
        .map((item) => {
          const netPhp = item.gross_php - item.expenses_php;
          return {
            ...item,
            label: item.key,
            net_php: netPhp,
            end_balance_php: netPhp - item.withdrawn_php,
          };
        })
        .sort((a, b) => a.key.localeCompare(b.key));

    return {
      daily: finalize(grouped.daily),
      monthly: finalize(grouped.monthly),
      yearly: finalize(grouped.yearly),
    };
  }, [filteredDashboardData, fxRates]);

  const insightAvailableYears = useMemo(() => {
    const years = new Set([new Date().getFullYear(), insightsDailyYear]);
    const rows = [
      ...(serverInsights.daily || []),
      ...(insightsData.daily || []),
    ];
    rows.forEach((row) => {
      const date = normalizeDate(row.label || row.key);
      if (!date) return;
      years.add(Number(date.slice(0, 4)));
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [insightsData.daily, insightsDailyYear, serverInsights.daily]);

  const selectedInsightRows = useMemo(() => {
    const serverRows = serverInsights[insightsView] || [];
    const baseRows = serverRows.length > 0 ? serverRows : (insightsData[insightsView] || []);
    const normalizedRows = baseRows.map((row) => {
      const grossPhp = safeNumber(row.gross_php);
      const expensesPhp = safeNumber(row.expenses_php);
      const withdrawnPhp = safeNumber(row.withdrawn_php);
      const netRaw = Number(row.net_php);
      const endBalanceRaw = Number(row.end_balance_php);
      const netPhp = Number.isFinite(netRaw) ? safeNumber(row.net_php) : (grossPhp - expensesPhp);
      const endBalancePhp = Number.isFinite(endBalanceRaw)
        ? safeNumber(row.end_balance_php)
        : (netPhp - withdrawnPhp);
      return {
        ...row,
        gross_php: grossPhp,
        expenses_php: expensesPhp,
        withdrawn_php: withdrawnPhp,
        net_php: netPhp,
        end_balance_php: endBalancePhp,
      };
    });
    if (insightsView !== 'daily') return normalizedRows;
    return normalizedRows.filter((row) => {
      const date = normalizeDate(row.label || row.key);
      if (!date) return false;
      const year = Number(date.slice(0, 4));
      const month = Number(date.slice(5, 7));
      return year === Number(insightsDailyYear) && month === Number(insightsDailyMonth);
    });
  }, [insightsData, insightsDailyMonth, insightsDailyYear, insightsView, serverInsights]);

  const selectedInsightSummary = useMemo(() => {
    const rows = selectedInsightRows;
    const totals = rows.reduce(
      (acc, row) => {
        acc.gross += safeNumber(row.gross_php);
        acc.expenses += safeNumber(row.expenses_php);
        acc.withdrawn += safeNumber(row.withdrawn_php);
        acc.net += safeNumber(row.net_php);
        acc.endBalance += safeNumber(row.end_balance_php);
        return acc;
      },
      { gross: 0, expenses: 0, withdrawn: 0, net: 0, endBalance: 0 }
    );
    const periods = rows.length;
    return {
      ...totals,
      periods,
      avgNet: periods > 0 ? totals.net / periods : 0,
    };
  }, [selectedInsightRows]);

  const insightTrend = useMemo(() => {
    if (selectedInsightRows.length < 2) {
      return { gross: 0, expenses: 0, net: 0 };
    }
    const latest = selectedInsightRows[selectedInsightRows.length - 1];
    const prev = selectedInsightRows[selectedInsightRows.length - 2];
    return {
      gross: safeNumber(latest.gross_php) - safeNumber(prev.gross_php),
      expenses: safeNumber(latest.expenses_php) - safeNumber(prev.expenses_php),
      net: safeNumber(latest.net_php) - safeNumber(prev.net_php),
    };
  }, [selectedInsightRows]);

  const formatInsightAxisLabel = (label) => {
    const raw = String(label || '');
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return formatShortDate(raw);
    if (/^\d{4}-\d{2}$/.test(raw)) {
      const [year, month] = raw.split('-').map(Number);
      const monthName = MONTH_OPTIONS.find((item) => item.value === month)?.label?.slice(0, 3) || raw;
      return `${monthName} ${String(year).slice(-2)}`;
    }
    return raw;
  };

  const renderInsightTooltip = ({ active, payload, label }) => {
    if (!active || !Array.isArray(payload) || payload.length === 0) return null;
    const row = payload[0]?.payload || {};
    const netValue = safeNumber(row.net_php);
    return (
      <Paper sx={{ p: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">{formatInsightAxisLabel(label)}</Typography>
        <Typography variant="body2">Gross: {formatAmount(row.gross_php)}</Typography>
        <Typography variant="body2">Expenses: {formatAmount(row.expenses_php)}</Typography>
        <Typography variant="body2">Withdrawn: {formatAmount(row.withdrawn_php)}</Typography>
        <Typography variant="body2" sx={{ color: netValue >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
          Net: {formatAmount(netValue)}
        </Typography>
      </Paper>
    );
  };

  const filteredRows = useMemo(() => {
    return rows;
  }, [rows]);

  const providerOptions = useMemo(() => {
    const map = new Map();
    (dashboardData.providers || []).forEach((p) => {
      const code = p.code || 'UNKNOWN';
      map.set(code, { code, name: p.name || code });
    });
    (dashboardData.earnings || []).forEach((item) => {
      const code = item.provider_code || 'UNKNOWN';
      if (!map.has(code)) map.set(code, { code, name: code });
    });
    return Array.from(map.values()).sort((a, b) => a.code.localeCompare(b.code));
  }, [dashboardData.providers, dashboardData.earnings]);

  useEffect(() => {
    const providerCodes = providerOptions.map((p) => p.code);
    setSelectedOverallProviders((prev) => {
      if (providerCodes.length === 0) return prev;
      if (prev === null) return providerCodes;
      const filtered = prev.filter((code) => providerCodes.includes(code));
      return filtered;
    });
  }, [providerOptions]);

  useEffect(() => {
    if (!Array.isArray(selectedOverallProviders)) return;
    localStorage.setItem(
      OVERALL_PROVIDER_STORAGE_KEY,
      JSON.stringify(selectedOverallProviders)
    );
  }, [selectedOverallProviders]);

  useEffect(() => {
    localStorage.setItem(OVERALL_DATE_FILTER_STORAGE_KEY, overallDateFilter);
  }, [overallDateFilter]);

  useEffect(() => {
    localStorage.setItem(OVERALL_DAILY_MONTH_STORAGE_KEY, String(overallDailyMonth));
  }, [overallDailyMonth]);

  useEffect(() => {
    localStorage.setItem(OVERALL_DAILY_YEAR_STORAGE_KEY, String(overallDailyYear));
  }, [overallDailyYear]);

  useEffect(() => {
    localStorage.setItem(DELETE_HISTORY_STORAGE_KEY, JSON.stringify(deleteHistory));
  }, [deleteHistory]);

  useEffect(() => {
    localStorage.setItem(AUDIT_LOG_STORAGE_KEY, JSON.stringify(auditLog));
  }, [auditLog]);

  useEffect(() => {
    localStorage.setItem(SHOW_BALANCES_STORAGE_KEY, showBalances ? '1' : '0');
  }, [showBalances]);

  useEffect(() => {
    localStorage.setItem(CURRENCY_VIEW_STORAGE_KEY, currencyView);
  }, [currencyView]);

  useEffect(() => {
    localStorage.setItem(INSIGHTS_DATE_FILTER_STORAGE_KEY, insightsView);
  }, [insightsView]);

  useEffect(() => {
    localStorage.setItem(INSIGHTS_DAILY_MONTH_STORAGE_KEY, String(insightsDailyMonth));
  }, [insightsDailyMonth]);

  useEffect(() => {
    localStorage.setItem(INSIGHTS_DAILY_YEAR_STORAGE_KEY, String(insightsDailyYear));
  }, [insightsDailyYear]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'n') {
        event.preventDefault();
        handleOpenCreate();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection]);

  const defaultSimRate = useMemo(() => {
    const simExpenses = (dashboardData.expenses || [])
      .filter((item) => item.type === 'sim' && item.sim_rate !== null && item.sim_rate !== undefined)
      .map((item) => Number(item.sim_rate))
      .filter((num) => Number.isFinite(num) && num > 0);
    if (simExpenses.length === 0) return 25;
    return simExpenses[simExpenses.length - 1];
  }, [dashboardData.expenses]);

  const overallProviderColumns = useMemo(() => {
    const selected = selectedOverallProviders || [];
    return providerOptions.filter((provider) => selected.includes(provider.code));
  }, [providerOptions, selectedOverallProviders]);

  const overallAvailableYears = useMemo(() => {
    const years = new Set([new Date().getFullYear(), overallDailyYear]);
    (serverOverallByDate || []).forEach((item) => {
      const date = normalizeDate(item.date);
      if (!date) return;
      years.add(Number(date.slice(0, 4)));
    });
    (filteredDashboardData.expenses || []).forEach((item) => {
      const date = normalizeDate(item.date);
      if (!date) return;
      years.add(Number(date.slice(0, 4)));
    });
    (filteredDashboardData.earnings || []).forEach((item) => {
      const date = normalizeDate(item.date);
      if (!date) return;
      years.add(Number(date.slice(0, 4)));
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [filteredDashboardData.earnings, filteredDashboardData.expenses, overallDailyYear, serverOverallByDate]);

  const overallByDate = useMemo(() => {
    const sortRows = (items) => [...items].sort((a, b) => {
      const aVal = a[overallSortField];
      const bVal = b[overallSortField];
      let result = 0;
      if (typeof aVal === 'number' || typeof bVal === 'number') {
        result = safeNumber(aVal) - safeNumber(bVal);
      } else {
        result = String(aVal ?? '').localeCompare(String(bVal ?? ''));
      }
      return overallSortOrder === 'asc' ? result : -result;
    });

    const groupDateKey = (date) => {
      if (overallDateFilter === 'yearly') return date.slice(0, 4);
      if (overallDateFilter === 'monthly') return date.slice(0, 7);
      return date;
    };

    const groupRows = (rows) => {
      if (overallDateFilter === 'all' || overallDateFilter === 'daily') return rows;
      const grouped = {};
      rows.forEach((row) => {
        const date = normalizeDate(row.date);
        if (!date) return;
        const key = groupDateKey(date);
        if (!grouped[key]) {
          grouped[key] = {
            date: key,
            sim_used: 0,
            expense_sim_php: 0,
            expense_load_php: 0,
            total_sms_eur: 0,
            total_voip_usd: 0,
            total_sms_php: 0,
            total_voip_php: 0,
            total_gross_php: 0,
            total_net_profit_php: 0,
            provider_earnings: {},
          };
        }
        grouped[key].sim_used += safeNumber(row.sim_used);
        grouped[key].expense_sim_php += safeNumber(row.expense_sim_php);
        grouped[key].expense_load_php += safeNumber(row.expense_load_php);
        grouped[key].total_sms_eur += safeNumber(row.total_sms_eur);
        grouped[key].total_voip_usd += safeNumber(row.total_voip_usd);
        grouped[key].total_sms_php += safeNumber(row.total_sms_php);
        grouped[key].total_voip_php += safeNumber(row.total_voip_php);
        grouped[key].total_gross_php += safeNumber(row.total_gross_php);
        grouped[key].total_net_profit_php += safeNumber(row.total_net_profit_php);
        Object.entries(row.provider_earnings || {}).forEach(([providerCode, amounts]) => {
          if (!grouped[key].provider_earnings[providerCode]) {
            grouped[key].provider_earnings[providerCode] = { sms_eur: 0, voip_usd: 0 };
          }
          grouped[key].provider_earnings[providerCode].sms_eur += safeNumber(amounts?.sms_eur);
          grouped[key].provider_earnings[providerCode].voip_usd += safeNumber(amounts?.voip_usd);
        });
      });
      return Object.values(grouped);
    };

    const filterDailyRows = (rows) => {
      if (overallDateFilter !== 'daily') return rows;
      return rows.filter((row) => {
        const date = normalizeDate(row.date);
        if (!date) return false;
        const year = Number(date.slice(0, 4));
        const month = Number(date.slice(5, 7));
        return year === Number(overallDailyYear) && month === Number(overallDailyMonth);
      });
    };

    if ((serverOverallByDate || []).length > 0) {
      return sortRows(groupRows(filterDailyRows(serverOverallByDate || [])));
    }
    const byDate = {};
    const ensureDate = (date) => {
      if (!byDate[date]) {
        byDate[date] = {
          date,
          sim_used: 0,
          expense_sim_php: 0,
          expense_load_php: 0,
          total_sms_eur: 0,
          total_voip_usd: 0,
          provider_earnings: {},
        };
      }
      return byDate[date];
    };

    (filteredDashboardData.expenses || []).forEach((item) => {
      const date = normalizeDate(item.date);
      if (!date) return;
      const row = ensureDate(date);
      const amount = safeNumber(item.amount);
      if (item.type === 'sim') {
        row.sim_used += safeNumber(item.sim_used);
        row.expense_sim_php += amount;
      } else if (item.type === 'load') {
        row.expense_load_php += amount;
      }
    });

    (filteredDashboardData.earnings || []).forEach((item) => {
      const date = normalizeDate(item.date);
      if (!date) return;
      const row = ensureDate(date);
      const providerCode = item.provider_code || 'UNKNOWN';
      if (!row.provider_earnings[providerCode]) {
        row.provider_earnings[providerCode] = { sms_eur: 0, voip_usd: 0 };
      }
      const amount = safeNumber(item.amount);
      if (item.channel === 'SMS' && item.currency === 'EUR') {
        row.total_sms_eur += amount;
        row.provider_earnings[providerCode].sms_eur += amount;
      }
      if (item.channel === 'VOIP' && item.currency === 'USD') {
        row.total_voip_usd += amount;
        row.provider_earnings[providerCode].voip_usd += amount;
      }
    });

    const usdToPhp = safeNumber(fxRates.usdToPhp);
    const eurToPhp = safeNumber(fxRates.eurToPhp);

    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
      today.getDate()
    ).padStart(2, '0')}`;

    const rows = Object.values(byDate)
      .filter((row) => row.date <= todayStr)
      .map((row) => {
        const totalSmsPhp = row.total_sms_eur * eurToPhp;
        const totalVoipPhp = row.total_voip_usd * usdToPhp;
        const totalGrossPhp = (row.total_sms_eur * eurToPhp) + (row.total_voip_usd * usdToPhp);
        const totalExpensePhp = row.expense_sim_php + row.expense_load_php;
        return {
          ...row,
          total_sms_php: totalSmsPhp,
          total_voip_php: totalVoipPhp,
          total_gross_php: totalGrossPhp,
          total_net_profit_php: totalGrossPhp - totalExpensePhp,
        };
      });
    return sortRows(groupRows(filterDailyRows(rows)));
  }, [filteredDashboardData.expenses, filteredDashboardData.earnings, fxRates, overallDailyMonth, overallDailyYear, overallDateFilter, overallSortField, overallSortOrder, serverOverallByDate]);

  const formatOverallDateLabel = (value) => {
    if (!value) return '';
    if (overallDateFilter === 'yearly') return String(value);
    if (overallDateFilter === 'monthly') return String(value);
    return formatShortDate(value);
  };

  const handleOpenCreate = () => {
    setEditingRecord(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (record) => {
    setEditingRecord(record);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    const isConfirmed = await confirm({
      title: 'Delete Record?',
      message: 'Are you sure you want to delete this record?',
    });
    if (!isConfirmed) return;

    try {
      const deletedRecord = rows.find((item) => String(item._id) === String(id)) || null;
      const response = await fetch(`${API_BASE}/${collection}/${id}`, { method: 'DELETE' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Delete failed');
      setUndoDelete({
        open: true,
        record: deletedRecord,
        collection,
      });
      if (deletedRecord) {
        const { _id, ...payload } = deletedRecord;
        setDeleteHistory((prev) => ([
          {
            id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            when: new Date().toISOString(),
            collection,
            payload,
          },
          ...prev,
        ]).slice(0, 100));
      }
      logAudit('delete', `${collection} record deleted`);
      showNotification('Record deleted. You can undo this action.', 'warning', {
        autoHideDuration: 6000,
        anchorOrigin: { vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' },
        action: (
          <Button color="secondary" size="small" onClick={handleUndoDelete}>
            UNDO
          </Button>
        ),
      });
      fetchRows();
      fetchDashboard();
    } catch (err) {
      showNotification(`Error: ${err.message}`, 'error');
    }
  };

  const handleUndoDelete = async () => {
    if (!undoDelete.record || !undoDelete.collection) return;
    try {
      const { _id, ...payload } = undoDelete.record;
      const response = await fetch(`${API_BASE}/${undoDelete.collection}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Undo failed');
      setDeleteHistory((prev) => prev.filter((item) => {
        if (item.collection !== undoDelete.collection) return true;
        return JSON.stringify(item.payload) !== JSON.stringify(payload);
      }));
      logAudit('undo_delete', `${undoDelete.collection} record restored via undo`);
      setUndoDelete({ open: false, record: null, collection: '' });
      showNotification('Delete undone successfully.', 'success');
      fetchRows();
      fetchDashboard();
    } catch (err) {
      showNotification(`Undo failed: ${err.message}`, 'error');
    }
  };

  const handleDialogSuccess = () => {
    logAudit(editingRecord ? 'edit' : 'create', `${collection} record ${editingRecord ? 'updated' : 'created'}`);
    setDialogOpen(false);
    setEditingRecord(null);
    fetchRows();
    fetchDashboard();
  };

  const handleToggleOverallProvider = (providerCode) => {
    setSelectedOverallProviders((prev) => {
      const current = prev || [];
      if (current.includes(providerCode)) return current.filter((code) => code !== providerCode);
      return [...current, providerCode];
    });
  };

  const handleToggleAllOverallProviders = (checked) => {
    if (checked) {
      setSelectedOverallProviders(providerOptions.map((p) => p.code));
    } else {
      setSelectedOverallProviders([]);
    }
  };

  const logAudit = (action, details) => {
    setAuditLog((prev) => ([
      {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        when: new Date().toISOString(),
        who: 'local-user',
        action,
        details,
      },
      ...prev,
    ]).slice(0, 300));
  };

  const handleSortRecords = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    setSortOrder('asc');
  };

  const handleSortOverall = (field) => {
    if (overallSortField === field) {
      setOverallSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setOverallSortField(field);
    setOverallSortOrder('asc');
  };

  const handleDrilldownFromInsight = (payload) => {
    const label = payload?.activeLabel || payload?.label;
    const { from, to } = toDateRangeByLabel(label);
    setActiveTab('records');
    setRecordsTab('records');
    setCollection('earnings');
    setRecordDateFrom(from);
    setRecordDateTo(to);
    setSearch('');
  };

  const handleRestoreDeleted = async (item) => {
    try {
      const response = await fetch(`${API_BASE}/${item.collection}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item.payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Restore failed');
      setDeleteHistory((prev) => prev.filter((row) => row.id !== item.id));
      logAudit('restore_deleted', `${item.collection} record restored from deleted history`);
      showNotification('Deleted record restored.', 'success');
      fetchRows();
      fetchDashboard();
    } catch (err) {
      showNotification(`Restore failed: ${err.message}`, 'error');
    }
  };

  const renderTrendChip = (delta, inverse = false) => {
    const positive = delta >= 0;
    const good = inverse ? !positive : positive;
    const label = `${positive ? '+' : ''}${formatAmount(delta)} vs prev`;
    return <Chip size="small" label={label} color={good ? 'success' : 'error'} />;
  };

  const displayBalance = (value) => (showBalances ? `${formatAmount(value)} ₱` : '******');
  const denseTableCellSx = isMobile ? { py: 0.75, px: 1, fontSize: '0.82rem', whiteSpace: 'normal', wordBreak: 'break-word' } : undefined;
  const mobileTableHeadSx = isMobile
    ? {
      '& th.MuiTableCell-head': {
        py: '4px !important',
        px: '6px !important',
        fontSize: '0.68rem !important',
        lineHeight: 1.1,
        whiteSpace: 'nowrap',
      },
      '& .MuiTableSortLabel-root': {
        fontSize: '0.68rem !important',
      },
    }
    : undefined;
  const visibleOverallProviderColumns = isMobile ? [] : overallProviderColumns;
  const dashboardMetricCards = [
    {
      key: 'net_current_month',
      label: 'Net Current Month Income (₱)',
      value: formatAmount(dashboardMetrics.currentMonthIncomePhp - dashboardMetrics.totalCurrentMonthExpensePhp),
      icon: PaidIcon,
      color: 'success',
    },
    {
      key: 'daily_average',
      label: 'Daily Average (₱)',
      value: formatAmount(dashboardMetrics.dailyAveragePhp),
      icon: TrendingUpIcon,
      color: 'info',
    },
    {
      key: 'month_projection',
      label: 'Month Projection Earnings (₱)',
      value: formatAmount(dashboardMetrics.monthProjectionPhp),
      icon: CalendarMonthIcon,
      color: 'primary',
    },
    {
      key: 'current_month_used_sim',
      label: 'Current Month Used Sim',
      value: formatAmount(dashboardMetrics.currentMonthUsedSim),
      icon: SavingsIcon,
      color: 'secondary',
    },
    {
      key: 'current_month_sim_expense',
      label: 'Current Month Sim Expense (₱)',
      value: formatAmount(dashboardMetrics.currentMonthSimExpensePhp),
      icon: WarningAmberIcon,
      color: 'warning',
    },
    {
      key: 'current_month_load_expense',
      label: 'Current Month Load Expense (₱)',
      value: formatAmount(dashboardMetrics.currentMonthLoadExpensePhp),
      icon: WarningAmberIcon,
      color: 'warning',
    },
    {
      key: 'total_current_month_expense',
      label: 'Total Current Month Expense (₱)',
      value: formatAmount(dashboardMetrics.totalCurrentMonthExpensePhp),
      icon: WarningAmberIcon,
      color: 'error',
    },
    {
      key: 'gross_current_month',
      label: 'Gross Current Month (₱)',
      value: formatAmount(dashboardMetrics.currentMonthIncomePhp),
      icon: PaidIcon,
      color: 'primary',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', py: { xs: 1, md: 4 } }}>
      <Container maxWidth="xl" disableGutters={isMobile} sx={{ px: { xs: 0.5, sm: 2, md: 3 } }}>
        <Stack spacing={{ xs: 2, md: 3 }}>

          <Paper sx={{ p: { xs: 1, sm: 1.5 }, borderRadius: 3 }}>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              variant={isMobile ? 'fullWidth' : 'standard'}
            >
              <Tab value="dashboard" label="Dashboard" />
              <Tab value="records" label="Records" />
            </Tabs>
          </Paper>

          {activeTab === 'dashboard' && (
            <Paper sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: 3 }}>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                  <Typography variant={isNarrow ? 'h6' : 'h5'}>Dashboard</Typography>
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <MuiTooltip title={showBalances ? 'Hide balances' : 'Show balances'}>
                      <IconButton size="small" onClick={() => setShowBalances((prev) => !prev)} aria-label={showBalances ? 'Hide balances' : 'Show balances'}>
                        {showBalances ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </MuiTooltip>
                    <MuiTooltip title="Refresh dashboard">
                      <IconButton size="small" onClick={fetchDashboard} aria-label="Refresh dashboard">
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </MuiTooltip>
                    <MuiTooltip title="Refresh FX">
                      <IconButton size="small" onClick={fetchFxRates} aria-label="Refresh FX">
                        <CurrencyExchangeIcon fontSize="small" />
                      </IconButton>
                    </MuiTooltip>
                  </Stack>
                </Stack>

                {dashboardError && <Alert severity="error">{dashboardError}</Alert>}
                {dashboardLoading && (
                  <Stack spacing={1}>
                    <Skeleton variant="rounded" height={28} />
                    <Skeleton variant="rounded" height={28} />
                  </Stack>
                )}
                {fxError && <Alert severity="warning">{fxError}</Alert>}
                {fxLoading && <Typography variant="body2">Loading live FX rates...</Typography>}

                <Typography variant="body2" color="text.secondary">
                  Live FX:
                  {' '}
                  $1 = {formatAmount(dashboardMetrics.usdToPhp)} ₱,
                  {' '}
                  €1 = {formatAmount(dashboardMetrics.eurToPhp)} ₱
                  {fxRates.lastUpdated ? ` (updated: ${fxRates.lastUpdated})` : ''}
                </Typography>

                <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'primary.main' }}>
                  <CardContent>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                      <AccountBalanceWalletIcon color="primary" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Available Balance (Top Priority)</Typography>
                        <Typography variant={isMobile ? (isNarrow ? 'h6' : 'h5') : 'h4'}>{displayBalance(dashboardMetrics.availableBalancePhp)}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>

                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      md: 'repeat(4, minmax(0, 1fr))',
                    },
                  }}
                >
                  {dashboardMetricCards.map((metric) => {
                    const MetricIcon = metric.icon;
                    return (
                      <Card key={metric.key} sx={{ width: '100%' }}>
                        <CardContent sx={{ display: 'flex', flexDirection: 'column', minHeight: { xs: 102, sm: 112 } }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ minHeight: 40 }}>
                            <MetricIcon fontSize="small" color={metric.color} />
                            <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
                              {metric.label}
                            </Typography>
                          </Stack>
                          <Typography variant="h6" sx={{ mt: 'auto' }}>{metric.value}</Typography>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>

                <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                      <Typography variant="h6">Insights</Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <TextField
                          size="small"
                          select
                          label="Date Filter"
                          value={insightsView}
                          onChange={(e) => setInsightsView(e.target.value)}
                          sx={{ minWidth: { xs: 160, sm: 180 } }}
                        >
                          <MenuItem value="daily">Daily</MenuItem>
                          <MenuItem value="yearly">Yearly</MenuItem>
                          <MenuItem value="monthly">Monthly</MenuItem>
                        </TextField>
                        {insightsView === 'daily' && (
                          <>
                            <TextField
                              size="small"
                              select
                              label="Month"
                              value={insightsDailyMonth}
                              onChange={(e) => setInsightsDailyMonth(Number(e.target.value))}
                              sx={{ minWidth: { xs: 140, sm: 150 } }}
                            >
                              {MONTH_OPTIONS.map((month) => (
                                <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              size="small"
                              select
                              label="Year"
                              value={insightsDailyYear}
                              onChange={(e) => setInsightsDailyYear(Number(e.target.value))}
                              sx={{ minWidth: { xs: 110, sm: 120 } }}
                            >
                              {insightAvailableYears.map((year) => (
                                <MenuItem key={year} value={year}>{year}</MenuItem>
                              ))}
                            </TextField>
                          </>
                        )}
                      </Stack>
                    </Stack>

                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6} md={3}><Card><CardContent sx={{ py: compactMode ? 1 : 2 }}><Typography variant="caption">Gross (₱)</Typography><Typography variant={isNarrow ? 'body1' : 'subtitle1'}>{formatAmount(selectedInsightSummary.gross)}</Typography><Box sx={{ mt: 1 }}>{renderTrendChip(insightTrend.gross)}</Box></CardContent></Card></Grid>
                      <Grid item xs={12} sm={6} md={3}><Card><CardContent sx={{ py: compactMode ? 1 : 2 }}><Typography variant="caption">Expenses (₱)</Typography><Typography variant={isNarrow ? 'body1' : 'subtitle1'}>{formatAmount(selectedInsightSummary.expenses)}</Typography><Box sx={{ mt: 1 }}>{renderTrendChip(insightTrend.expenses, true)}</Box></CardContent></Card></Grid>
                      <Grid item xs={12} sm={6} md={3}><Card><CardContent sx={{ py: compactMode ? 1 : 2 }}><Typography variant="caption">Withdrawn (₱)</Typography><Typography variant={isNarrow ? 'body1' : 'subtitle1'}>{formatAmount(selectedInsightSummary.withdrawn)}</Typography></CardContent></Card></Grid>
                      <Grid item xs={12} sm={6} md={3}><Card><CardContent sx={{ py: compactMode ? 1 : 2 }}><Typography variant="caption">Net (₱)</Typography><Typography variant={isNarrow ? 'body1' : 'subtitle1'}>{formatAmount(selectedInsightSummary.net)}</Typography><Box sx={{ mt: 1 }}>{renderTrendChip(insightTrend.net)}</Box></CardContent></Card></Grid>
                      <Grid item xs={12} sm={6} md={3}><Card><CardContent sx={{ py: compactMode ? 1 : 2 }}><Typography variant="caption">Avg Net / Period (₱)</Typography><Typography variant={isNarrow ? 'body1' : 'subtitle1'}>{formatAmount(selectedInsightSummary.avgNet)}</Typography></CardContent></Card></Grid>
                    </Grid>

                    <Box
                      sx={{
                        width: '100%',
                        height: { xs: 290, sm: 380 },
                        borderRadius: 2.5,
                        p: { xs: 1, sm: 1.25 },
                        border: '1px solid',
                        borderColor: 'divider',
                        background: 'linear-gradient(180deg, rgba(25,118,210,0.08) 0%, rgba(46,125,50,0.02) 55%, rgba(0,0,0,0) 100%)',
                      }}
                    >
                      <ResponsiveContainer>
                        <ComposedChart data={selectedInsightRows} onClick={handleDrilldownFromInsight} margin={{ top: 8, right: 8, left: -14, bottom: 0 }}>
                          <defs>
                            <linearGradient id="insightGross" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#ef6c00" stopOpacity={0.55} />
                              <stop offset="100%" stopColor="#ef6c00" stopOpacity={0.05} />
                            </linearGradient>
                            <linearGradient id="insightExpenses" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#d32f2f" stopOpacity={0.35} />
                              <stop offset="100%" stopColor="#d32f2f" stopOpacity={0.03} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="4 6" vertical={false} opacity={0.35} />
                          <XAxis
                            dataKey="label"
                            tickFormatter={formatInsightAxisLabel}
                            minTickGap={24}
                            tick={{ fontSize: isMobile ? 10 : 11 }}
                          />
                          <YAxis tickFormatter={(value) => formatAmount(value)} tick={{ fontSize: isMobile ? 10 : 11 }} width={isMobile ? 56 : 74} />
                          <ReferenceLine y={0} stroke="#607d8b" strokeDasharray="3 3" />
                          <Tooltip content={renderInsightTooltip} />
                          <Area type="monotone" dataKey="gross_php" name="Gross (₱)" stroke="#ef6c00" strokeWidth={2.2} fill="url(#insightGross)" />
                          <Area type="monotone" dataKey="expenses_php" name="Expenses (₱)" stroke="#d32f2f" strokeWidth={1.8} fill="url(#insightExpenses)" />
                          <Line
                            type="monotone"
                            dataKey="net_php"
                            name="Net (₱)"
                            stroke="#2e7d32"
                            strokeWidth={2.6}
                            dot={false}
                            activeDot={{ r: 4 }}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Box>
                  </Stack>
                </Paper>

                <Typography variant="h6">Provider Breakdown</Typography>
                <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                  <Table size={compactMode || isMobile ? 'small' : 'medium'} stickyHeader sx={{ minWidth: isMobile ? 980 : 860 }}>
                    <TableHead sx={mobileTableHeadSx}>
                      <TableRow>
                        <TableCell sx={{ ...denseTableCellSx, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 2 }}>provider</TableCell>
                        <TableCell sx={denseTableCellSx} align="right">
                          {currencyView === 'phpOnly' ? 'earnings sms (₱)' : 'earnings sms (€)'}
                        </TableCell>
                        <TableCell sx={denseTableCellSx} align="right">
                          {currencyView === 'phpOnly' ? 'earnings voip (₱)' : 'earnings voip ($)'}
                        </TableCell>
                        <TableCell sx={denseTableCellSx} align="right">
                          {currencyView === 'phpOnly' ? 'withdrawn sms (₱)' : 'withdrawn sms (€)'}
                        </TableCell>
                        <TableCell sx={denseTableCellSx} align="right">
                          {currencyView === 'phpOnly' ? 'withdrawn voip (₱)' : 'withdrawn voip ($)'}
                        </TableCell>
                        <TableCell sx={denseTableCellSx} align="right">balance sms (₱)</TableCell>
                        <TableCell sx={denseTableCellSx} align="right">balance voip (₱)</TableCell>
                        <TableCell sx={denseTableCellSx} align="right">total balance (₱)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dashboardMetrics.providers.map((item) => (
                        <TableRow key={item.provider_code}>
                          <TableCell sx={{ ...denseTableCellSx, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>{item.provider_code}</TableCell>
                          <TableCell sx={denseTableCellSx} align="right">
                            {currencyView === 'phpOnly'
                              ? formatAmount(item.earnings_sms_eur * dashboardMetrics.eurToPhp)
                              : formatAmount(item.earnings_sms_eur)}
                          </TableCell>
                          <TableCell sx={denseTableCellSx} align="right">
                            {currencyView === 'phpOnly'
                              ? formatAmount(item.earnings_voip_usd * dashboardMetrics.usdToPhp)
                              : formatAmount(item.earnings_voip_usd)}
                          </TableCell>
                          <TableCell sx={denseTableCellSx} align="right">
                            {currencyView === 'phpOnly'
                              ? formatAmount(item.withdrawn_sms_eur * dashboardMetrics.eurToPhp)
                              : formatAmount(item.withdrawn_sms_eur)}
                          </TableCell>
                          <TableCell sx={denseTableCellSx} align="right">
                            {currencyView === 'phpOnly'
                              ? formatAmount(item.withdrawn_voip_usd * dashboardMetrics.usdToPhp)
                              : formatAmount(item.withdrawn_voip_usd)}
                          </TableCell>
                          <TableCell sx={denseTableCellSx} align="right">{showBalances ? formatAmount(item.balance_sms_eur * dashboardMetrics.eurToPhp) : '******'}</TableCell>
                          <TableCell sx={denseTableCellSx} align="right">{showBalances ? formatAmount(item.balance_voip_usd * dashboardMetrics.usdToPhp) : '******'}</TableCell>
                          <TableCell sx={denseTableCellSx} align="right">
                            {showBalances
                              ? formatAmount(
                                ((item.earnings_sms_eur - item.withdrawn_sms_eur) * dashboardMetrics.eurToPhp) +
                                ((item.earnings_voip_usd - item.withdrawn_voip_usd) * dashboardMetrics.usdToPhp)
                              )
                              : '******'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </Paper>
          )}

          {activeTab === 'records' && (
            <Paper sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: 3 }}>
                  <Stack spacing={2}>
                    <Tabs
                      value={recordsTab}
                      onChange={(_, value) => setRecordsTab(value)}
                      variant="scrollable"
                      allowScrollButtonsMobile
                    >
                      <Tab value="records" label="Records" />
                      <Tab value="overall" label="Overall By Date" />
                      <Tab value="deleted" label="Deleted History" />
                    </Tabs>

                    <Divider />

                    {recordsTab === 'records' && (
                      <>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                        {COLLECTIONS.map((item) => (
                          <Chip
                            key={item.value}
                            size={isNarrow ? 'small' : 'medium'}
                            label={item.label}
                            color={collection === item.value ? 'primary' : 'default'}
                            onClick={() => setCollection(item.value)}
                          />
                        ))}
                      </Stack>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button fullWidth={isMobile} variant="contained" onClick={handleOpenCreate}>
                          Add {COLLECTIONS.find((c) => c.value === collection)?.label}
                        </Button>
                        <Button fullWidth={isMobile} variant="outlined" onClick={fetchRows}>Refresh</Button>
                      </Stack>
                    </Stack>

                    <Divider />

                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                      <Typography variant="h6">
                        {COLLECTIONS.find((c) => c.value === collection)?.label} Records ({rows.length} / {recordTotal})
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <TextField fullWidth={isMobile} size="small" label="From" type="date" value={recordDateFrom} onChange={(e) => { setRecordDateFrom(e.target.value); setRecordPage(1); }} InputLabelProps={{ shrink: true }} />
                        <TextField fullWidth={isMobile} size="small" label="To" type="date" value={recordDateTo} onChange={(e) => { setRecordDateTo(e.target.value); setRecordPage(1); }} InputLabelProps={{ shrink: true }} />
                        <MuiTooltip title="Shortcut: Ctrl+K">
                          <TextField
                            inputRef={searchInputRef}
                            fullWidth={isMobile}
                            size="small"
                            label="Search"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setRecordPage(1); }}
                            sx={{ minWidth: { xs: 0, sm: 220 } }}
                          />
                        </MuiTooltip>
                      </Stack>
                    </Stack>

                    {error && <Alert severity="error">{error}</Alert>}

                    <TableContainer component={Paper} sx={{ maxHeight: '70vh', overflowX: 'auto' }}>
                      <Table size={compactMode || isMobile ? 'small' : 'medium'} stickyHeader sx={{ minWidth: isMobile ? 620 : 720 }}>
                        <TableHead sx={mobileTableHeadSx}>
                          <TableRow>
                            {fields.map((field) => (
                              <TableCell
                                key={field}
                                sx={field === fields[0]
                                  ? { ...denseTableCellSx, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 2 }
                                  : denseTableCellSx}
                              >
                                <TableSortLabel
                                  active={sortField === field}
                                  direction={sortField === field ? sortOrder : 'asc'}
                                  onClick={() => handleSortRecords(field)}
                                >
                                  {field}
                                </TableSortLabel>
                              </TableCell>
                            ))}
                            <TableCell sx={denseTableCellSx} align="center">actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {loading ? (
                            [...Array(5)].map((_, index) => (
                              <TableRow key={`loading-${index}`}>
                                <TableCell sx={denseTableCellSx} colSpan={fields.length + 1}><Skeleton variant="rounded" height={24} /></TableCell>
                              </TableRow>
                            ))
                          ) : filteredRows.length === 0 ? (
                            <TableRow><TableCell sx={denseTableCellSx} colSpan={fields.length + 1}>{rows.length === 0 ? 'No records yet. Add a transaction or provider to start.' : 'No matching records. Try widening date/filter criteria.'}</TableCell></TableRow>
                          ) : (
                            filteredRows.map((row) => (
                              <TableRow key={row._id} hover>
                                {fields.map((field) => (
                                  <TableCell
                                    key={`${row._id}-${field}`}
                                    sx={field === fields[0]
                                      ? { ...denseTableCellSx, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }
                                      : denseTableCellSx}
                                  >
                                    {toDisplayValue(field, row[field])}
                                  </TableCell>
                                ))}
                                <TableCell sx={denseTableCellSx} align="center">
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center">
                                    <Button fullWidth={isMobile} size="small" variant="outlined" onClick={() => handleOpenEdit(row)}>Edit</Button>
                                    <Button fullWidth={isMobile} size="small" variant="outlined" color="error" onClick={() => handleDelete(row._id)}>Delete</Button>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                        <Typography variant="body2">Page Size</Typography>
                        <TextField
                          size="small"
                          select
                          value={recordPageSize}
                          onChange={(e) => {
                            setRecordPageSize(Number(e.target.value));
                            setRecordPage(1);
                          }}
                          sx={{ width: 90 }}
                        >
                          {[25, 50, 100, 200].map((size) => (
                            <MenuItem key={size} value={size}>{size}</MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                        <Typography variant="body2">
                          Page {recordPage} / {Math.max(1, Math.ceil(recordTotal / recordPageSize))}
                        </Typography>
                        <Button
                          fullWidth={isMobile}
                          variant="outlined"
                          size="small"
                          disabled={recordPage <= 1}
                          onClick={() => setRecordPage((prev) => Math.max(1, prev - 1))}
                        >
                          Prev
                        </Button>
                        <Button
                          fullWidth={isMobile}
                          variant="outlined"
                          size="small"
                          disabled={recordPage >= Math.max(1, Math.ceil(recordTotal / recordPageSize))}
                          onClick={() => setRecordPage((prev) => prev + 1)}
                        >
                          Next
                        </Button>
                      </Stack>
                    </Stack>
                      </>
                    )}

                    {recordsTab === 'overall' && (
                      <>
                    <Stack spacing={1}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                        <Typography variant="h6">Overall By Date</Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                          <TextField
                            size="small"
                            select
                            label="Date Filter"
                            value={overallDateFilter}
                            onChange={(e) => setOverallDateFilter(e.target.value)}
                            sx={{ minWidth: { xs: 160, sm: 180 } }}
                          >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="daily">Daily</MenuItem>
                            <MenuItem value="yearly">Yearly</MenuItem>
                            <MenuItem value="monthly">Monthly</MenuItem>
                          </TextField>
                          {overallDateFilter === 'daily' && (
                            <>
                              <TextField
                                size="small"
                                select
                                label="Month"
                                value={overallDailyMonth}
                                onChange={(e) => setOverallDailyMonth(Number(e.target.value))}
                                sx={{ minWidth: { xs: 140, sm: 150 } }}
                              >
                                {MONTH_OPTIONS.map((month) => (
                                  <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                                ))}
                              </TextField>
                              <TextField
                                size="small"
                                select
                                label="Year"
                                value={overallDailyYear}
                                onChange={(e) => setOverallDailyYear(Number(e.target.value))}
                                sx={{ minWidth: { xs: 110, sm: 120 } }}
                              >
                                {overallAvailableYears.map((year) => (
                                  <MenuItem key={year} value={year}>{year}</MenuItem>
                                ))}
                              </TextField>
                            </>
                          )}
                        </Stack>
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        Provider checklist affects only visible earnings columns. Totals always include all providers.
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                        <FormControlLabel
                          sx={{ mr: 0 }}
                          control={
                            <Checkbox
                              checked={providerOptions.length > 0 && (selectedOverallProviders || []).length === providerOptions.length}
                              indeterminate={
                                (selectedOverallProviders || []).length > 0 &&
                                (selectedOverallProviders || []).length < providerOptions.length
                              }
                              onChange={(e) => handleToggleAllOverallProviders(e.target.checked)}
                            />
                          }
                          label="All providers"
                        />
                        {providerOptions.map((provider) => (
                          <FormControlLabel
                            key={provider.code}
                            sx={{ mr: 0 }}
                            control={
                              <Checkbox
                                checked={(selectedOverallProviders || []).includes(provider.code)}
                                onChange={() => handleToggleOverallProvider(provider.code)}
                              />
                            }
                            label={provider.code}
                          />
                        ))}
                      </Stack>
                    </Stack>

                    <TableContainer component={Paper} sx={{ maxHeight: '55vh', overflowX: 'auto' }}>
                      <Table size={compactMode || isMobile ? 'small' : 'medium'} stickyHeader sx={{ minWidth: isMobile ? 700 : 1100 }}>
                        <TableHead sx={mobileTableHeadSx}>
                          <TableRow>
                              <TableCell sx={{ ...denseTableCellSx, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 2 }}>
                                <TableSortLabel
                                  active={overallSortField === 'date'}
                                  direction={overallSortField === 'date' ? overallSortOrder : 'asc'}
                                  onClick={() => handleSortOverall('date')}
                                >
                                  date
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={denseTableCellSx} align="right">
                                <TableSortLabel
                                  active={overallSortField === 'sim_used'}
                                  direction={overallSortField === 'sim_used' ? overallSortOrder : 'asc'}
                                  onClick={() => handleSortOverall('sim_used')}
                                >
                                  sim used
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={denseTableCellSx} align="right">
                                <TableSortLabel
                                  active={overallSortField === 'expense_sim_php'}
                                  direction={overallSortField === 'expense_sim_php' ? overallSortOrder : 'asc'}
                                  onClick={() => handleSortOverall('expense_sim_php')}
                                >
                                  sim exp (₱)
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={denseTableCellSx} align="right">
                                <TableSortLabel
                                  active={overallSortField === 'expense_load_php'}
                                  direction={overallSortField === 'expense_load_php' ? overallSortOrder : 'asc'}
                                  onClick={() => handleSortOverall('expense_load_php')}
                                >
                                  load exp (₱)
                                </TableSortLabel>
                              </TableCell>
                              {visibleOverallProviderColumns.map((provider) => (
                                <React.Fragment key={provider.code}>
                                  <TableCell sx={denseTableCellSx} align="right">
                                    {currencyView === 'phpOnly' ? `${provider.code} sms (₱)` : `${provider.code} sms (€)`}
                                  </TableCell>
                                  <TableCell sx={denseTableCellSx} align="right">
                                    {currencyView === 'phpOnly' ? `${provider.code} voip (₱)` : `${provider.code} voip ($)`}
                                  </TableCell>
                                </React.Fragment>
                              ))}
                              <TableCell sx={denseTableCellSx} align="right">
                                <TableSortLabel
                                  active={overallSortField === 'total_sms_php'}
                                  direction={overallSortField === 'total_sms_php' ? overallSortOrder : 'asc'}
                                  onClick={() => handleSortOverall('total_sms_php')}
                                >
                                  gross sms (₱)
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={denseTableCellSx} align="right">
                                <TableSortLabel
                                  active={overallSortField === 'total_voip_php'}
                                  direction={overallSortField === 'total_voip_php' ? overallSortOrder : 'asc'}
                                  onClick={() => handleSortOverall('total_voip_php')}
                                >
                                  gross voip (₱)
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={denseTableCellSx} align="right">
                                <TableSortLabel
                                  active={overallSortField === 'total_gross_php'}
                                  direction={overallSortField === 'total_gross_php' ? overallSortOrder : 'asc'}
                                  onClick={() => handleSortOverall('total_gross_php')}
                                >
                                  total gross (₱)
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={denseTableCellSx} align="right">
                                <TableSortLabel
                                  active={overallSortField === 'total_net_profit_php'}
                                  direction={overallSortField === 'total_net_profit_php' ? overallSortOrder : 'asc'}
                                  onClick={() => handleSortOverall('total_net_profit_php')}
                                >
                                  net (₱)
                                </TableSortLabel>
                              </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {overallByDate.length === 0 ? (
                            <TableRow>
                              <TableCell sx={denseTableCellSx} colSpan={8 + (visibleOverallProviderColumns.length * 2)}>
                                No overall records for this filter range.
                              </TableCell>
                            </TableRow>
                          ) : (
                            overallByDate.map((row) => (
                              <TableRow key={row.date}>
                                  <TableCell sx={{ ...denseTableCellSx, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>{formatOverallDateLabel(row.date)}</TableCell>
                                  <TableCell sx={denseTableCellSx} align="right">{formatAmount(row.sim_used)}</TableCell>
                                  <TableCell sx={denseTableCellSx} align="right">{formatAmount(row.expense_sim_php)}</TableCell>
                                  <TableCell sx={denseTableCellSx} align="right">{formatAmount(row.expense_load_php)}</TableCell>
                                  {visibleOverallProviderColumns.map((provider) => {
                                    const data = row.provider_earnings[provider.code] || { sms_eur: 0, voip_usd: 0 };
                                    return (
                                      <React.Fragment key={`${row.date}-${provider.code}`}>
                                        <TableCell sx={denseTableCellSx} align="right">
                                          {currencyView === 'phpOnly'
                                            ? formatAmount(data.sms_eur * safeNumber(fxRates.eurToPhp))
                                            : formatAmount(data.sms_eur)}
                                        </TableCell>
                                        <TableCell sx={denseTableCellSx} align="right">
                                          {currencyView === 'phpOnly'
                                            ? formatAmount(data.voip_usd * safeNumber(fxRates.usdToPhp))
                                            : formatAmount(data.voip_usd)}
                                        </TableCell>
                                      </React.Fragment>
                                    );
                                  })}
                                  <TableCell sx={denseTableCellSx} align="right">{formatAmount(row.total_sms_php)}</TableCell>
                                  <TableCell sx={denseTableCellSx} align="right">{formatAmount(row.total_voip_php)}</TableCell>
                                  <TableCell sx={denseTableCellSx} align="right">{formatAmount(row.total_gross_php)}</TableCell>
                                  <TableCell sx={denseTableCellSx} align="right">{formatAmount(row.total_net_profit_php)}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                      </>
                    )}

                    {recordsTab === 'deleted' && (
                      <Paper sx={{ p: { xs: 1.25, sm: 2 }, borderRadius: 2 }}>
                        <Typography variant="h6">Soft-Delete History</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Deleted records are tracked locally with timestamp and can be restored.
                        </Typography>
                        <TableContainer component={Paper} sx={{ mt: 1, overflowX: 'auto' }}>
                          <Table size={compactMode || isMobile ? 'small' : 'medium'} sx={{ minWidth: isMobile ? 560 : 0 }}>
                            <TableHead sx={mobileTableHeadSx}>
                              <TableRow>
                                <TableCell sx={denseTableCellSx}>when</TableCell>
                                <TableCell sx={denseTableCellSx}>collection</TableCell>
                                <TableCell sx={denseTableCellSx}>preview</TableCell>
                                <TableCell sx={denseTableCellSx} align="right">action</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {deleteHistory.length === 0 ? (
                                <TableRow>
                                  <TableCell sx={denseTableCellSx} colSpan={4}>No deleted history.</TableCell>
                                </TableRow>
                              ) : (
                                deleteHistory.map((item) => (
                                  <TableRow key={item.id}>
                                    <TableCell sx={denseTableCellSx}>{formatShortDate(item.when)}</TableCell>
                                    <TableCell sx={denseTableCellSx}>{item.collection}</TableCell>
                                    <TableCell sx={denseTableCellSx}>{JSON.stringify(item.payload).slice(0, 120)}</TableCell>
                                    <TableCell sx={denseTableCellSx} align="right">
                                      <Button size="small" startIcon={<RestoreIcon />} onClick={() => handleRestoreDeleted(item)}>
                                        Restore
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        <Typography variant="h6" sx={{ mt: 2 }}>Audit Trail</Typography>
                        <TableContainer component={Paper} sx={{ mt: 1, overflowX: 'auto' }}>
                          <Table size={compactMode || isMobile ? 'small' : 'medium'} sx={{ minWidth: isMobile ? 560 : 0 }}>
                            <TableHead sx={mobileTableHeadSx}>
                              <TableRow>
                                <TableCell sx={denseTableCellSx}>when</TableCell>
                                <TableCell sx={denseTableCellSx}>who</TableCell>
                                <TableCell sx={denseTableCellSx}>action</TableCell>
                                <TableCell sx={denseTableCellSx}>details</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {auditLog.length === 0 ? (
                                <TableRow><TableCell sx={denseTableCellSx} colSpan={4}>No audit logs yet.</TableCell></TableRow>
                              ) : (
                                auditLog.slice(0, 50).map((log) => (
                                  <TableRow key={log.id}>
                                    <TableCell sx={denseTableCellSx}>{formatShortDate(log.when)}</TableCell>
                                    <TableCell sx={denseTableCellSx}>{log.who}</TableCell>
                                    <TableCell sx={denseTableCellSx}>{log.action}</TableCell>
                                    <TableCell sx={denseTableCellSx}>{log.details}</TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    )}
                  </Stack>
            </Paper>
          )}
        </Stack>
      </Container>

      <AddSmsVoipRecordDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingRecord(null);
        }}
        onSuccess={handleDialogSuccess}
        apiBase={API_BASE}
        collection={collection}
        recordToEdit={editingRecord}
        providers={providerOptions}
        defaultSimRate={defaultSimRate}
      />
    </Box>
  );
}

const SmsVoipManager = () => (
  <ThemeProvider>
    <ConfirmationProvider>
      <CssBaseline />
      <SmsVoipManagerContent />
    </ConfirmationProvider>
  </ThemeProvider>
);

export default SmsVoipManager;

